import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTenant } from "../context/TenantContext";
import {
  FaBell,
  FaBookOpen,
  FaCalendarAlt,
  FaChartLine,
  FaBars,
  FaTimes,
  FaChevronRight,
  FaChevronLeft,
  FaClipboardList,
  FaChalkboardTeacher,
  FaMoneyBillWave,
  FaShieldAlt,
  FaUserGraduate,
  FaUsers,
  FaImages,
  FaChartPie,
} from "react-icons/fa";

export const ADMIN_SIDEBAR_ACTIONS = [
  {
    title: "Plan Usage",
    description: "Review the remaining capacity on the current school plan.",
    path: "/admin/plan-usage",
    icon: FaChartPie,
    tone: "bg-cyan-50 text-cyan-700",
    freeOnly: true,
    directRoute: true,
  },
  {
    title: "School Website",
    description: "Customize homepage sliders, achievements, upcoming events, and announcements.",
    path: "/admin/home-slider",
    icon: FaImages,
    tone: "bg-cyan-50 text-cyan-700",
    directRoute: true,
  },
  {
    title: "Student Management",
    description: "Manage student profiles, guardian contacts, class placement, and fee readiness.",
    path: "/admin/students",
    module: "Students",
    icon: FaUserGraduate,
    tone: "bg-emerald-50 text-emerald-700",
  },
  {
    title: "Teacher Management",
    description: "Maintain teacher profiles, qualifications, assignments, and salary details.",
    path: "/admin/teachers",
    module: "Teachers",
    icon: FaChalkboardTeacher,
    tone: "bg-emerald-50 text-emerald-700",
  },
  {
    title: "Classes & Subjects",
    description: "Manage classes, sections, subjects, and teacher mapping before timetable setup.",
    path: "/admin/class-subjects",
    module: "Classes & Subjects",
    icon: FaBookOpen,
    tone: "bg-sky-50 text-sky-700",
  },
  {
    title: "Timetable Planner",
    description: "Plan day-period slots, assign rooms, and catch teacher or class conflicts early.",
    path: "/admin/timetable",
    module: "Timetable",
    icon: FaCalendarAlt,
    tone: "bg-indigo-50 text-indigo-700",
  },
  {
    title: "Exam Management",
    description: "Create, schedule, and manage exams.",
    path: "/admin/exams",
    module: "Exams",
    icon: FaClipboardList,
    tone: "bg-violet-50 text-violet-700",
  },
  {
    title: "Fee Management",
    description: "Track invoices, collection, pending dues, and payment status across students.",
    path: "/admin/fees",
    module: "Fees",
    icon: FaMoneyBillWave,
    tone: "bg-amber-50 text-amber-700",
  },
  {
    title: "Student Activity Monitor",
    description: "Track student usage time, inactive users, and no-usage students.",
    path: "/admin/students-activity",
    icon: FaChartLine,
    tone: "bg-indigo-50 text-indigo-700",
  },
  {
    title: "Notifications",
    description: "Publish announcements, reminders, and alerts by audience and channel.",
    path: "/admin/notifications",
    module: "Notifications",
    icon: FaBell,
    tone: "bg-sky-50 text-sky-700",
  },
  {
    title: "Learning Resources",
    description: "Request approved notes and books for student classes.",
    path: "/admin/learning-resources",
    icon: FaBookOpen,
    tone: "bg-sky-50 text-sky-700",
    directRoute: true,
  },
  {
    title: "Permission Matrix",
    description: "Control role-based access and approval requirements across modules.",
    path: "/admin/permissions",
    module: "Permissions",
    icon: FaShieldAlt,
    tone: "bg-emerald-50 text-emerald-700",
  },
  {
    title: "Parent Portal",
    description: "Link guardians to students and manage parent portal access status.",
    path: "/admin/parent-portal",
    module: "Parent Portal",
    icon: FaUsers,
    tone: "bg-orange-50 text-orange-700",
  },
  {
    title: "Customization Requests",
    description: "Request configurable module tab changes from Super Admin.",
    path: "/admin/customization-requests",
    module: "Students",
    icon: FaClipboardList,
    tone: "bg-violet-50 text-violet-700",
    premiumOnly: true,
    directRoute: true,
  },
  {
    title: "Admissions",
    description: "Review applications and keep admission workflows moving.",
    path: "/admin/admissions",
    module: "Admissions",
    icon: FaClipboardList,
    tone: "bg-amber-50 text-amber-700",
    hiddenInSidebar: true,
  },
  {
    title: "Schools & Users",
    description: "Manage school users and tenant operations.",
    path: "/admin/users",
    module: "Users",
    icon: FaUsers,
    tone: "bg-cyan-50 text-cyan-700",
    hiddenInSidebar: true,
  },
];

const ADMIN_SIDEBAR_ACTIVE_KEY = "adminSidebar:activeModule";
const SIDEBAR_CONFIG_KEY_BY_PATH = {
  "/admin/students": "students", "/admin/teachers": "teachers", "/admin/class-subjects": "classes_subjects",
  "/admin/timetable": "timetable", "/admin/exams": "exams", "/admin/fees": "fees",
  "/admin/students-activity": "student_activity", "/admin/notifications": "notifications",
  "/admin/learning-resources": "learning_resources",
  "/admin/permissions": "permissions", "/admin/parent-portal": "parents",
};

const readStoredActivePath = (actions = []) => {
  try {
    const stored = window.localStorage.getItem(ADMIN_SIDEBAR_ACTIVE_KEY);
    if (stored && actions.some((action) => action.path === stored)) return stored;
  } catch {
    // Ignore storage failures; fall back to the first available module.
  }
  return actions[0]?.path || "";
};

const AdminSidebar = ({
  actions = ADMIN_SIDEBAR_ACTIONS,
  activePath = "",
  onSelectAction,
  role = "",
  permissionRules = [],
  permissionReady = false,
  isCollapsed = false,
  onToggleCollapsed,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { activeTenant, tenantFeatures, isSubdomainTenant, tenantLogoUrl, schoolName: contextSchoolName } = useTenant();
  const isSubdomain = Boolean(isSubdomainTenant || (activeTenant?.subdomain && activeTenant?.brandingEnabled === true));
  const showWhiteLabelBranding = activeTenant?.brandingEnabled !== false && activeTenant?.whiteLabelEnabled === true;
  const schoolName = contextSchoolName || activeTenant?.schoolName || activeTenant?.name || "";
  const schoolLogo = tenantLogoUrl || activeTenant?.logoUrl || activeTenant?.schoolLogoUrl;
  const isPremiumCustomizationPlan = activeTenant?.subscription?.status === "ACTIVE"
    && ["PREMIUM", "GROWTH", "PRO", "ENTERPRISE"].includes(String(activeTenant?.subscription?.plan || "").trim().toUpperCase());
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const [dashboardActivePath, setDashboardActivePath] = useState(() => activePath || readStoredActivePath(actions));

  useEffect(() => {
    const closeMobileSidebar = () => setIsMobileSidebarOpen(false);
    window.addEventListener("admin-sidebar:close", closeMobileSidebar);
    return () => window.removeEventListener("admin-sidebar:close", closeMobileSidebar);
  }, []);

  useEffect(() => {
    const syncSelectedModule = (event) => {
      const requestedPath = event?.detail?.path;
      if (!requestedPath || !actions.some((action) => action.path === requestedPath)) return;
      setDashboardActivePath(requestedPath);
      try {
        window.localStorage.setItem(ADMIN_SIDEBAR_ACTIVE_KEY, requestedPath);
      } catch {
        // Keep the active selection in memory when storage is unavailable.
      }
    };
    window.addEventListener("admin-dashboard:select-action", syncSelectedModule);
    return () => window.removeEventListener("admin-dashboard:select-action", syncSelectedModule);
  }, [actions]);

  useEffect(() => {
    const requestedPath = location.state?.adminModulePath;
    if (!requestedPath || !actions.some((action) => action.path === requestedPath)) return;

    setDashboardActivePath(requestedPath);
    try {
      window.localStorage.setItem(ADMIN_SIDEBAR_ACTIVE_KEY, requestedPath);
    } catch {
      // Keep the in-memory state active when storage is unavailable.
    }
    onSelectAction?.(requestedPath);
    window.dispatchEvent(new CustomEvent("admin-dashboard:select-action", {
      detail: { path: requestedPath },
    }));
  }, [actions, location.state?.adminModulePath, onSelectAction]);

  const isActionActive = (action) => {
    if (location.pathname === "/admin-home") {
      return dashboardActivePath === action.path;
    }

    if (location.pathname === "/admin-board") {
      return dashboardActivePath === action.path;
    }

    const actionPath = action.routePath || action.path;
    return location.pathname === actionPath || location.pathname.startsWith(`${actionPath}/`);
  };

  const handleActionClick = (action) => {
    if (action.disabled) return;
    setIsMobileSidebarOpen(false);
    if (action.directRoute) {
      navigate(action.routePath || action.path);
      return;
    }
    setDashboardActivePath(action.path);
    try {
      window.localStorage.setItem(ADMIN_SIDEBAR_ACTIVE_KEY, action.path);
    } catch {
      // Keep the in-memory state active when storage is unavailable.
    }
    onSelectAction?.(action.path);
    window.dispatchEvent(new CustomEvent("admin-dashboard:select-action", {
      detail: { path: action.path },
    }));
    navigate(action.routePath || action.path);
  };

  const visibleActions = actions.filter((action) => {
    const sideMenuKey = SIDEBAR_CONFIG_KEY_BY_PATH[action.path];
    return !action.hiddenInSidebar
      && (!action.premiumOnly || isPremiumCustomizationPlan)
      && (!action.freeOnly || !isPremiumCustomizationPlan)
      && (!sideMenuKey || tenantFeatures?.adminSidebar?.items?.[sideMenuKey] !== false);
  });
  const academicPaths = ["/admin/students", "/admin/teachers", "/admin/class-subjects", "/admin/timetable", "/admin/exams"];
  const operationsPaths = ["/admin/fees", "/admin/students-activity", "/admin/notifications"];
  const sections = [
    { label: "Main navigation", items: visibleActions.filter((action) => academicPaths.includes(action.path)) },
    { label: "Operations", items: visibleActions.filter((action) => operationsPaths.includes(action.path)) },
    { label: "Administration", items: visibleActions.filter((action) => !academicPaths.includes(action.path) && !operationsPaths.includes(action.path)) },
  ];
  const renderSidebarInner = (compact = false, collapsed = false) => (
    <div className="flex h-full min-h-0 flex-col bg-[#17202b]">
      {compact ? (
        <div className="flex items-center justify-between border-b border-white/5 px-4 py-4">
          <div>
            <p className="text-sm font-semibold text-white">Admin workspace</p>
            <p className="mt-1 text-[9px] font-bold uppercase tracking-[0.18em] text-slate-500">Navigation</p>
          </div>
          <button type="button" aria-label="Close sidebar" onClick={() => setIsMobileSidebarOpen(false)} className="inline-flex rounded-md p-2 text-slate-400 transition hover:bg-white/5 hover:text-white"><FaTimes /></button>
        </div>
      ) : (
        <div className={`border-b border-white/5 ${collapsed ? "px-3 py-4" : "px-5 py-5"}`}>
          <div className={`flex items-center ${collapsed ? "justify-center" : "gap-3"}`}>
            {showWhiteLabelBranding ? (
              schoolLogo && !collapsed ? (
                <div className="flex h-9 w-9 items-center justify-center overflow-hidden rounded-lg bg-white/10 p-0.5">
                  <img src={schoolLogo} alt={`${schoolName} logo`} className="h-full w-full object-contain" />
                </div>
              ) : null
            ) : (
              !collapsed && <img src={`${process.env.PUBLIC_URL}/logo.png`} alt="ClassNX" className="h-9 w-9 rounded-lg object-cover" />
            )}
            {!collapsed && <div className="min-w-0 flex-1">
              {showWhiteLabelBranding ? (
                <p className="truncate text-sm font-black tracking-tight text-white">{schoolName || "Admin workspace"}</p>
              ) : (
                <p className="text-sm font-black tracking-tight text-white">Class<span className="text-sky-400">NX</span></p>
              )}
              <p className="mt-0.5 text-[9px] font-bold uppercase tracking-[0.18em] text-slate-400">Admin workspace</p>
            </div>}
            <button type="button" onClick={onToggleCollapsed} aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"} title={collapsed ? "Expand sidebar" : "Collapse sidebar"} className={`flex shrink-0 items-center justify-center transition duration-200 ${collapsed ? "h-10 w-10 rounded-full border border-cyan-400/25 bg-cyan-400/[0.08] text-cyan-300 shadow-[0_8px_24px_-12px_rgba(34,211,238,0.8)] hover:scale-105 hover:border-cyan-300/50 hover:bg-cyan-400/[0.14] hover:text-cyan-200" : "h-8 w-8 rounded-md border border-white/[0.08] text-slate-400 hover:bg-white/[0.06] hover:text-white"}`}>{collapsed ? <FaChevronRight className="translate-x-px text-sm" /> : <FaChevronLeft />}</button>
          </div>
        </div>
      )}
      <nav className={`min-h-0 flex-1 overflow-y-auto py-4 smooth-scrollbar ${collapsed ? "px-2" : "px-3"}`} aria-label="Admin modules">
        <div className="space-y-6">
          {sections.filter((section) => section.items.length > 0).map((section) => (
            <section key={section.label}>
              {!collapsed && <p className="mb-2 px-3 text-[9px] font-bold uppercase tracking-[0.2em] text-slate-500">{section.label}</p>}
              <div className="space-y-1">
                {section.items.map((action) => {
                  const Icon = action.icon;
                  const isActive = isActionActive(action);
                  return (
                    <React.Fragment key={action.path}>
                    <button
                      type="button"
                      disabled={action.disabled}
                      onClick={() => handleActionClick(action)}
                      title={action.disabled ? `${action.title} is currently disabled` : action.title}
                      aria-current={isActive ? "page" : undefined}
                      className={`group flex w-full items-center rounded-md border-l-2 py-2.5 text-left text-[13px] font-medium transition ${collapsed ? "justify-center px-2" : "gap-3 px-3"} ${action.disabled ? "cursor-not-allowed border-transparent text-slate-500 opacity-40" : isActive ? "border-cyan-400 bg-white/[0.08] text-white" : "border-transparent text-slate-400 hover:bg-white/[0.04] hover:text-slate-100"}`}
                    >
                      <span className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-xs transition ${isActive ? "bg-cyan-400/10 text-cyan-300" : "bg-white/[0.035] text-slate-400 group-hover:text-slate-200"}`}><Icon /></span>
                      {!collapsed && <span className="min-w-0 flex-1 truncate">{action.title}</span>}
                      {!collapsed && <FaChevronRight className={`text-[8px] transition ${isActive ? "text-cyan-300" : "text-slate-600 group-hover:text-slate-400"}`} />}
                    </button>
                    </React.Fragment>
                  );
                })}
              </div>
            </section>
          ))}
        </div>
      </nav>
    </div>
  );

  return (
    <>
      <button
        type="button"
        onClick={() => setIsMobileSidebarOpen(true)}
        aria-label="Open admin menu"
        aria-expanded={isMobileSidebarOpen}
        className="theme-menu-fab fixed bottom-6 right-6 z-40 inline-flex h-14 w-14 items-center justify-center rounded-full border shadow-lg lg:hidden"
        title="Open menu"
      >
        <FaBars className="text-xl" />
      </button>
      {isMobileSidebarOpen && (
        <button type="button" aria-label="Close menu overlay" onClick={() => setIsMobileSidebarOpen(false)} className="theme-menu-overlay fixed inset-0 z-30 lg:hidden" />
      )}
      <aside
        className="fixed bottom-[4.25rem] left-0 top-[4.5rem] z-40 w-[82vw] max-w-[17rem] overflow-hidden border-r border-slate-800 bg-[#17202b] text-slate-100 shadow-2xl lg:hidden"
        style={{ transform: isMobileSidebarOpen ? "translateX(0)" : "translateX(-100%)", transition: "transform 300ms ease", visibility: isMobileSidebarOpen ? "visible" : "hidden" }}
      >
        {renderSidebarInner(true)}
      </aside>
      <aside className={`fixed bottom-0 left-0 top-0 z-[60] hidden flex-col overflow-hidden border-r border-slate-800 bg-[#17202b] text-slate-100 shadow-xl transition-[width] duration-300 lg:flex ${isCollapsed ? "w-[76px]" : "w-[248px]"}`}>
        {renderSidebarInner(false, isCollapsed)}
      </aside>
    </>
  );
};

export default AdminSidebar;
